import * as React from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { Styler, StylerProps } from '../../Styler';
import { useScrollspyContext } from '../Scrollspy.context';

export interface ScrollspyTargetProps extends StylerProps {
    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * A unique value for the target.
     */
    value: string;
}

export function ScrollspyTarget(props: Readonly<ScrollspyTargetProps>): React.JSX.Element {
    const { children, value, ref, ...other } = props;

    const { registerTarget, deregisterTarget, activeValue } = useScrollspyContext();

    const rootRef = React.useRef<HTMLElement | null>(null);
    const handleRef = useMergeRefs([ref, rootRef]);

    const active = value === activeValue;

    React.useEffect(() => {
        const el = rootRef.current;
        if (el) {
            // Register the target on mount
            registerTarget?.(el);
        }

        return () => {
            if (el) {
                // Deregister the target on unmount
                deregisterTarget?.(el);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Styler ref={handleRef} data-scrollspy-target={true} data-value={value} data-active={active} {...other}>
            {children}
        </Styler>
    );
}
